import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { ResourceService } from '../../../services/resource.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';

interface Office {
  code: string;
  description: string;
  active: boolean;
  resourceCount: number;
  letterCode: string | null;
  country: string | null;
}

@Component({
  selector: 'app-offices',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    RouterModule,
    ErrorStateComponent,
  ],
  templateUrl: './offices.component.html',
  styleUrls: ['./offices.component.scss'],
})
export class OfficesComponent implements OnInit {
  private router = inject(Router);
  private resourceService = inject(ResourceService);

  displayedColumns: string[] = ['code', 'letterCode', 'description', 'country', 'resourceCount', 'status'];
  dataSource = new MatTableDataSource<Office>([]);
  searchForm = new FormGroup({});

  allOffices: Office[] = [];
  filteredOffices: Office[] = [];

  isLoading = false;
  error: string | null = null;

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [10, 25, 50];

  // Sorting
  sortField = 'code';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Search
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadOffices();
  }

  loadOffices(): void {
    this.isLoading = true;
    this.error = null;

    this.resourceService.getUniqueOffices().subscribe({
      next: (offices) => {
        this.allOffices = offices;
        this.applyFilter(this.searchQuery);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading offices:', err);
        this.error = 'Failed to load offices. Please try again.';
        this.isLoading = false;
      },
    });
  }

  applyFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredOffices = [...this.allOffices];
    } else {
      this.filteredOffices = this.allOffices.filter(
        (office) =>
          office.code.toLowerCase().includes(term) ||
          office.description.toLowerCase().includes(term) ||
          (office.letterCode ?? '').toLowerCase().includes(term) ||
          (office.country ?? '').toLowerCase().includes(term),
      );
    }

    this.applySortToFiltered();
    this.totalCount = this.filteredOffices.length;
    this.pageIndex = 0;
    this.updateDataSource();
  }

  private updateDataSource(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.dataSource.data = this.filteredOffices.slice(start, end);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updateDataSource();
  }

  private applySortToFiltered(): void {
    if (!this.sortField || !this.sortDirection) return;

    this.filteredOffices.sort((a, b) => {
      const aValue = this.getSortValue(a, this.sortField);
      const bValue = this.getSortValue(b, this.sortField);

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  sortOffices(sort: Sort): void {
    this.sortField = sort.active;
    this.sortDirection = sort.direction as 'asc' | 'desc';

    this.applySortToFiltered();
    this.updateDataSource();
  }

  private getSortValue(office: Office, field: string): string | number {
    switch (field) {
      case 'code':
        return office.code.toLowerCase();
      case 'description':
        return office.description.toLowerCase();
      case 'resourceCount':
        return office.resourceCount;
      case 'letterCode':
        return (office.letterCode ?? '').toLowerCase();
      case 'country':
        return (office.country ?? '').toLowerCase();
      default:
        return '';
    }
  }

  navigateToDetails(officeCode: string): void {
    this.router.navigate(['/admin/offices', officeCode]);
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applyFilter(this.searchQuery);
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter('');
  }
}
